import type { Adb } from "@yume-chan/adb";
import { FILE_PERMISSION } from "../constants";

/** Check if an error is an AbortError (from AbortController cancellation) */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Create a ReadableStream from a Uint8Array (used by ADB sync.write) */
export function uint8ArrayToReadableStream(
  data: Uint8Array,
  onProgress?: (bytesSent: number) => void,
): ReadableStream<Uint8Array> {
  if (!onProgress) {
    return new ReadableStream({
      start(controller) {
        controller.enqueue(data);
        controller.close();
      },
    });
  }

  const CHUNK_SIZE = 256 * 1024;
  let offset = 0;
  return new ReadableStream({
    pull(controller) {
      if (offset >= data.byteLength) {
        controller.close();
        return;
      }
      const end = Math.min(offset + CHUNK_SIZE, data.byteLength);
      controller.enqueue(data.subarray(offset, end));
      offset = end;
      onProgress(offset);
    },
  });
}

/**
 * Download a file from URL with chunked progress tracking.
 * Returns the concatenated bytes and the total size (from content-length header).
 */
export async function downloadWithProgress(
  url: string,
  signal: AbortSignal,
  onProgress: (percent: number) => void,
): Promise<{ data: Uint8Array<ArrayBuffer>; totalSize: number }> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const totalSize = Number(response.headers.get("content-length") || 0);
  const body = response.body;
  if (!body) throw new Error("No response body");

  const chunks: Uint8Array[] = [];
  const reader = body.getReader();
  let bytesRead = 0;
  let done = false;

  while (!done) {
    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
    const result = await reader.read();
    done = result.done;
    if (!done && result.value) {
      chunks.push(result.value);
      bytesRead += result.value.byteLength;
      if (totalSize > 0) {
        onProgress(Math.round((bytesRead / totalSize) * 100));
      }
    }
  }

  const data = new Uint8Array(bytesRead);
  let offset = 0;
  for (const chunk of chunks) {
    data.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return { data, totalSize };
}

/**
 * Push a file to the device via ADB sync.
 * Handles sync lifecycle (create → write → dispose).
 */

export async function pushFileToDevice(
  adb: Adb,
  path: string,
  data: Uint8Array,
  permission = FILE_PERMISSION,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const sync = await adb.sync();
  try {
    const totalBytes = data.byteLength;
    const progressCb = onProgress
      ? (bytesSent: number) => onProgress(Math.round((bytesSent / totalBytes) * 100))
      : undefined;
    const writeOptions = {
      filename: path,
      permission,
      file: uint8ArrayToReadableStream(data, progressCb),
    };
    await sync.write(writeOptions);
  } finally {
    await sync.dispose();
  }
}
